import { I18n } from 'i18n-js';
import lv from './locales/lv.json';
import en from './locales/en.json';
import ru from './locales/ru.json';

type TranslationTree = Record<string, unknown>;
type LocaleMap = Record<string, TranslationTree>;

function isPlainObject(value: unknown): value is TranslationTree {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

export function deepMerge(base: TranslationTree, override: TranslationTree): TranslationTree {
  const result: TranslationTree = { ...base };

  Object.entries(override).forEach(([key, value]) => {
    const current = result[key];
    if (isPlainObject(current) && isPlainObject(value)) {
      result[key] = deepMerge(current, value);
    } else {
      result[key] = value;
    }
  });

  return result;
}

function unwrapLocaleFile(payload: unknown, locale: string): TranslationTree {
  if (!isPlainObject(payload)) return {};
  const nested = payload[locale];
  return isPlainObject(nested) ? nested : payload;
}

function bundledTranslations(): LocaleMap {
  return {
    lv: unwrapLocaleFile(lv, 'lv'),
    en: unwrapLocaleFile(en, 'en'),
    ru: unwrapLocaleFile(ru, 'ru'),
  };
}

function pageTranslations(): LocaleMap | null {
  if (typeof document === 'undefined') return null;

  const node = document.getElementById('i18n-translations');
  if (!node?.textContent?.trim()) return null;

  try {
    const parsed = JSON.parse(node.textContent) as unknown;
    return isPlainObject(parsed) ? (parsed as LocaleMap) : null;
  } catch {
    return null;
  }
}

function withLatvianFallback(all: LocaleMap): LocaleMap {
  const base = all.lv ?? {};
  return {
    lv: base,
    en: deepMerge(base, all.en ?? {}),
    ru: deepMerge(base, all.ru ?? {}),
  };
}

const translations = withLatvianFallback(pageTranslations() ?? bundledTranslations());
const i18n = new I18n(translations);

i18n.defaultLocale = 'lv';
i18n.locale = 'lv';
i18n.enableFallback = true;

function applyLocaleFromDocument(): void {
  if (typeof document === 'undefined') return;

  const body = document.body;
  const locale = body?.dataset.locale || 'lv';
  const defaultLocale = body?.dataset.defaultLocale || 'lv';

  i18n.locale = locale;
  i18n.defaultLocale = defaultLocale;
  document.documentElement.lang = locale;
}

applyLocaleFromDocument();

if (typeof document !== 'undefined') {
  document.addEventListener('DOMContentLoaded', applyLocaleFromDocument);
}

export default i18n;
